import type { WaterBillTarget } from "../../models/WaterBilling";
import { TableBody, TableCell, TableRow, Typography } from "@mui/material";
import {
	applyTrackedGallons,
	recalculateBillTotals,
	WaterBillGridRow,
	WaterBillPeriod,
} from "../../utils/billRows";
import { BillRow } from "./BillRow";
import { useBillEditing } from "./hooks/useBillEditing";
import { useTrackedGallonsPreview } from "./hooks/useTrackedGallonsPreview";

type CustomTableBodyProps = {
	rows: WaterBillGridRow[];
	draftRows: WaterBillGridRow[];
	targets: WaterBillTarget[];
	editingBillId: number | null;
	setEditingBillId: (billId: number | null) => void;
	setDraftRows: React.Dispatch<React.SetStateAction<WaterBillGridRow[]>>;
	periods: WaterBillPeriod[];
	onBillSave: (rows: WaterBillGridRow[]) => void;
	onBillDelete: (billId: number) => void;
};

export const CustomTableBody = ({
	rows,
	draftRows,
	targets,
	editingBillId,
	setEditingBillId,
	setDraftRows,
	periods,
	onBillSave,
	onBillDelete,
}: CustomTableBodyProps) => {
	const editing = useBillEditing({
		rows,
		draftRows,
		setDraftRows,
		targetCodes: targets.map((allocation) => allocation.TargetCode),
		periods,
		editingBillId,
		setEditingBillId,
		onBillSave,
	});

	const editedHeader =
		editingBillId === null
			? null
			: (draftRows.find(
					(row) => row.billId === editingBillId && row.isFirstOfBill,
				) ?? null);

	const { tracked, isLoading: isTrackedLoading } = useTrackedGallonsPreview({
		billId: editingBillId,
		start: editedHeader?.servicePeriodStart ?? null,
		end: editedHeader?.servicePeriodEnd ?? null,
		enabled: editingBillId !== null && editing.isPeriodDirty(editingBillId),
	});

	const recalculatedRows =
		editingBillId === null
			? draftRows
			: recalculateBillTotals(draftRows, editingBillId);
	const visibleRows =
		editingBillId === null || tracked === undefined
			? recalculatedRows
			: applyTrackedGallons(recalculatedRows, editingBillId, tracked);

	if (draftRows.length === 0)
		return (
			<TableBody>
				<TableRow>
					<TableCell colSpan={15 + targets.length * 3} sx={{ border: 0 }}>
						<Typography
							sx={{
								color: "text.secondary",
								fontSize: "0.85rem",
								py: 6,
								textAlign: "center",
							}}
						>
							No bills entered for this account yet.
						</Typography>
					</TableCell>
				</TableRow>
			</TableBody>
		);

	return (
		<TableBody>
			{visibleRows.map((row) => (
				<BillRow
					key={row.id}
					row={row}
					targets={targets}
					periods={periods}
					editing={editing}
					isTrackedLoading={isTrackedLoading}
					isTrackedFresh={tracked !== undefined}
					onBillDelete={onBillDelete}
				/>
			))}
		</TableBody>
	);
};
